import React, { Suspense, useEffect, useMemo, useRef, useState } from "react";
import {
  Mail,
  PhoneCall,
  CheckCircle,
  Copy,
  XCircle,
  Pencil,
  Check,
  Calendar,
  Clock,
  User,
  Plus,
  CopyCheck,
  ThumbsUp,
  ThumbsDown,
  SquarePen,
  Briefcase,
  Trophy,
  Eye,
  Puzzle,
  PlusIcon,
  Settings2,
  PanelLeft,
  ChevronLeft,
  PanelRight,
  X,
  Trash2,
} from "lucide-react";
import { useLocation, useParams } from "react-router";
import { useApiCall } from "../../../../component/custom-hooks/api-call";
import moment from "moment/moment";
import { Alert, Snackbar } from "@mui/material";
import Tooltip from "../../../../component/ui/tooltip";
import FilesTab from "../../../../component/files";
import Style from "./style.module.css";
import Swal from "sweetalert2";
import CommentsSection from "../../../../component/comment";
import InnerLoader from "../../../../component/ui/loader/inner-loader";
import EmployeeDirectoryAddUpdate from "../../../organization/emplyee-directory/add-update";
import NewEditIcon from "../../../../component/icons/action-edit-icon";
import { TableCard } from "../../../../component/table/table-layout";
import CustomizeTabsOverviewPopup from "./component/customize-tabs-popup";
import DotsVerticalIcon from "../../../../component/icons/dots-vertical";
import Chip from "../../../../component/ui/chip";
import StatusChip from "../../../../component/ui/chip/status";
import { RightSidebar } from "./component/right-side-bar";
import DealInfo from "./component/DealTab";
import ProjectTab from "./component/ProjectTab";
import Avatar from "../../../../component/ui/avatar";
import AvatarProfile from "../../../../component/ui/profile";
import { useClickOutside } from "../../../../component/custom-hooks/click-outside";

const Chip2 = ({ label, tone = "info" }) => {
  const toneMap = {
    success: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    danger: "bg-rose-50 text-rose-700 ring-rose-200",
    warning: "bg-amber-50 text-amber-700 ring-amber-200",
    info: "bg-sky-50 text-sky-700 ring-sky-200",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${toneMap[tone]}`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${
          tone === "success"
            ? "bg-emerald-500"
            : tone === "danger"
              ? "bg-rose-500"
              : tone === "warning"
                ? "bg-amber-500"
                : "bg-sky-500"
        }`}
      />
      {label}
    </span>
  );
};

function Field({
  label,
  value,
  icon: Icon,
  copyable,
  link,
  endTag = false,
  LastElement,
}) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="grid grid-cols-12 items-center gap-2 rounded-xl border border-solid border-slate-200 bg-white p-3 transition hover:bg-slate-50 hover:shadow transition-all duration-200">
      <div className="col-span-4 flex items-center gap-2 text-[13px] font-medium text-slate-500">
        {Icon ? <Icon className="size-4" /> : null}
        {label}
      </div>
      <div className="col-span-8 flex items-center justify-between gap-2 text-[13px] text-slate-800">
        {link ? (
          <a
            href={link}
            className="truncate underline decoration-slate-300 underline-offset-2 hover:text-blue-600 hover:decoration-blue-400"
          >
            {value}
          </a>
        ) : (
          <span className="truncate">{value}</span>
        )}
        {copyable || endTag ? (
          copyable ? (
            <button
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(String(value ?? ""));
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1500);
                } catch {}
              }}
              className="inline-flex items-center gap-1 rounded-md border border-solid border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-100"
            >
              <Copy className="size-3.5" /> {copied ? "Copied" : "Copy"}
            </button>
          ) : (
            <LastElement />
          )
        ) : null}
      </div>
    </div>
  );
}

const CopyIcon = ({ text }) => {
  const [isCopied, setIsCopied] = useState(false);

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setIsCopied(true);
      setTimeout(() => {
        setIsCopied(false);
      }, 3000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <>
      <div
        className="ms-2 hover:opacity-50 cursor-pointer transition-all"
        onClick={() => {
          copyToClipboard(text);
        }}
      >
        {isCopied ? (
          <CopyCheck size={15} color="#333" />
        ) : (
          <Copy size={15} color="#333" />
        )}
      </div>
    </>
  );
};

function NewField({ label, value, icon: Icon }) {
  const [isActive, setIsActive] = useState(false);
  const isUrl =
    typeof value === "string" &&
    (value.startsWith("http://") || value.startsWith("https://"));
  const [showAll, setShowAll] = useState(false);

  const limit = 70;
  const isLongText = value?.length > limit;

  const displayText =
    !isLongText || showAll ? value : value.slice(0, limit) + "...";
  return (
    <div>
      {isUrl ? (
        <>
          <div className="flex flex-col gap-y-[10px]">
            <div className="flex gap-[10px] items-center">
              {Icon ? (
                <span>{Icon ? <Icon size={16} /> : <User size={16} />} </span>
              ) : null}
              <p className="text-xs font-medium">{label}</p>
            </div>

            <div className="flex justify-between text-[14px] w-full font-normal text-[#999] min-w-0">
              <a
                href={value}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-500 hover:underline break-all whitespace-normal min-w-0"
              >
                {value?.length > 50 ? `${value?.slice(0, 30)}...` : value}
              </a>

              <CopyIcon text={value} />
            </div>
          </div>
        </>
      ) : (
        <div className="flex flex-col gap-y-[10px]">
          <div className="flex gap-[10px] items-center">
            {Icon ? (
              <span>{Icon ? <Icon size={16} /> : <User size={16} />} </span>
            ) : null}
            <p className="text-xs font-medium">{label}</p>
          </div>

          {/* <div className="text-sm font-medium text-[rgba(14,18,27,0.40)]">
            {value}
          </div> */}
          {/* Value */}
          <div className="text-sm font-medium text-[rgba(14,18,27,0.40)] break-words whitespace-normal">
            {displayText}

            {isLongText && (
              <button
                onClick={() => setShowAll(!showAll)}
                className="ml-1 text-[#0057FB] text-xs font-medium hover:underline"
              >
                {showAll ? "Show less" : "Show more"}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const Details = ({ leadDetail }) => {
  const campaignKeys = [
    "parent_url",
    "user_agent",
    "campaigned",
    "is_dropoff",
    "adgroupid",
    "keyword",
    "device",
    "matchtype",
    "msclkid",
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
    "gclid",
  ];

  const excludeKeys = [
    "name",
    "email",
    "phone",
    "lead_source",
    "leadstatus",
    "leadtime",
    "country",
    "date",
    "signuptime",
    "lead_converted_time",
  ];

  const campaignMeta = leadDetail?.meta?.filter((m) =>
    campaignKeys.includes(m.meta_key),
  );

  const otherMeta = leadDetail?.meta?.filter(
    (m) =>
      !campaignKeys.includes(m.meta_key) && !excludeKeys.includes(m.meta_key),
  );
  return (
    <>
      {/* Additonal  Information */}

      <div className="flex flex-col gap-y-[17px]">
        <div className="bg-white   p-5 rounded-[10px] border border-[#E1E4EA]">
          {/* Additonal Details */}
          <div className="flex flex-col gap-y-[10px]">
            <div className="flex justify-between items-center">
              <p className="text-xl font-medium leading-normal">
                Additional Details
              </p>
            </div>

            <div className="grid grid-cols-3 gap-[15px] ">
              <div className="py-[17px] flex flex-col gap-y-[34px] col-span-1">
                <NewField
                  label="Industry"
                  value={leadDetail?.industry || "-"}
                />
                <NewField label="Source" value={leadDetail?.source || "-"} />
                <NewField
                  label="Created At"
                  value={leadDetail?.signupdate || "-"}
                />
              </div>

              <div className="py-[17px] flex flex-col gap-y-[34px] col-span-1">
                <NewField label="Badge" value={leadDetail?.badge || "-"} />
                <NewField
                  label="Page Source"
                  value={leadDetail?.page_source || "-"}
                />
                <NewField
                  label="Lead Converted Time"
                  value={leadDetail?.lead_converted_time || "-"}
                />
              </div>
            </div>
          </div>
        </div>

        {campaignMeta?.length > 0 || otherMeta?.length > 0 ? (
          <>
            {/* Other Information */}
            <div className="bg-white   p-5 rounded-[10px] border border-[#E1E4EA]">
              {/* Other Information*/}
              <div className="flex flex-col gap-y-[10px]">
                {/* Header Section */}
                <div className="flex justify-between items-center">
                  <p className="text-xl font-medium leading-normal">
                    Other Information
                  </p>

                  {/* <div className="flex gap-[2px] justify-center cursor-pointer">
                <SquarePen size={20} />
                <span className="text-sm font-medium text-[#999]">
                  Edit Info
                </span>
              </div> */}
                </div>

                <div>
                  {campaignMeta?.length > 0 && (
                    <div className="grid gap-[15px] grid-cols-3">
                      {campaignMeta.map((st, i) => (
                        <div className="col-span-1 min-w-0">
                          <NewField
                            key={i}
                            label={st.meta_key
                              .replaceAll("_", " ")
                              .replace(/\b\w/g, (char) => char.toUpperCase())}
                            value={st.meta_value || "-"}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  {otherMeta?.length > 0 && (
                    <div className="grid gap-[15px] grid-cols-3">
                      {otherMeta.map((st, i) => (
                        <div className="col-span-1 min-w-0">
                          <NewField
                            key={i}
                            label={st.meta_key
                              .replaceAll("_", " ")
                              .replace(/\b\w/g, (char) => char.toUpperCase())}
                            value={st.meta_value || "-"}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </>
  );
};

const TransactionTabs = ({ leadDetail }) => {
  return (
    <>
      <div className="space-y-3">
        {leadDetail?.transactions?.map((tx) => (
          <Field
            key={tx?.transactionid}
            label={tx?.transactiontime}
            value={`$${tx.amount}`}
            endTag={true}
            LastElement={() => (
              <StatusChip
                color={
                  tx.transactionstatus == "success"
                    ? "green"
                    : tx.transactionstatus == "pending"
                      ? "blue"
                      : "red"
                }
              >
                {tx.transactionstatus}
              </StatusChip>
            )}
            icon={CheckCircle}
          />
        ))}
      </div>
    </>
  );
};

const CommentsTab = ({ leadDetail }) => {
  return (
    <>
      <div className="space-y-4 !h-full">
        <CommentsSection
          type="contact"
          id={leadDetail?.userid}
          apiBaseUrl="contact"
          title="true"
        />
      </div>
    </>
  );
};

const TimelineTab = ({ leadDetail }) => {
  return (
    <>
      <div className="space-y-4">
        <div className="flex flex-col gap-y-[17px]">
          <div className="bg-white   p-5 rounded-[10px] border border-[#E1E4EA]">
            {/* Additonal Details */}
            <div className="flex flex-col gap-y-[10px]">
              <div className="flex justify-between items-center">
                <p className="text-xl font-medium leading-normal">Timeline</p>

                {/* <div className="flex gap-[2px] justify-center cursor-pointer">
                  <SquarePen size={20} />
                  <span className="text-sm font-medium text-[#999]">
                    Edit Info
                  </span>
                </div> */}
              </div>
            </div>

            <div className="flex flex-col h-full mt-2">
              <div className="flex-1 overflow-y-auto pr-4">
                <div className="relative ml-2 mt-2">
                  {leadDetail?.activities?.length ? (
                    <div className="absolute left-3 top-0 h-full border-l-2 border-gray-200"></div>
                  ) : null}
                  {leadDetail?.activities?.length ? (
                    leadDetail.activities.map((act, i) => {
                      const userName = act?.user?.name || "U";
                      const userInitial = userName.charAt(0).toUpperCase();
                      const colors = [
                        "bg-emerald-500",
                        "bg-blue-500",
                        "bg-indigo-500",
                        "bg-rose-500",
                      ];
                      const userColor = "bg-slate-200";

                      return (
                        <div key={i} className="relative pl-10 pb-6">
                          {/* ✅ User Initial Circle */}
                          <div
                            className={`absolute left-0 top-0 flex items-center justify-center w-7 h-7 rounded-full bg-slate-200 text-slate-700 text-xs font-semibold`}
                          >
                            {userInitial}
                          </div>

                          {/* ✅ Activity content properly aligned */}
                          <div className="flex flex-col gap-1 pt-0.5">
                            <div className="flex justify-between items-center">
                              <p className="text-sm font-medium text-gray-800">
                                {act.log}
                              </p>
                              <p className="text-xs text-gray-400">
                                {moment(act.logtime).fromNow()}
                              </p>
                            </div>

                            {act?.user?.name && (
                              <div className="flex items-center gap-1 text-xs text-gray-500">
                                <User className="h-3 w-3 text-gray-400" />
                                <span className="leading-none">
                                  by {act.user.name}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-gray-400">No timeline available.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const FollowUpsTab = ({ leadDetail, onError, onSubmit }) => {
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  return (
    <>
      <div className="bg-white rounded-[10px] p-5  border border-[#e1e4ea]  flex flex-col gap-[10px]">
        {/* ✅ Activity Cards */}
        {leadDetail?.schedules?.length ? (
          <div className="space-y-3">
            {leadDetail.schedules.map((sch, i) => {
              const isCompleted = sch.status?.toLowerCase() === "completed";
              const isPending = sch.status?.toLowerCase() === "pending";
              const isHigh = sch.priority?.toLowerCase() === "high";

              const typeColors = {
                meeting: "bg-blue-100 text-blue-700",
                call: "bg-emerald-100 text-emerald-700",
                email: "bg-indigo-100 text-indigo-700",
                task: "bg-amber-100 text-amber-700",
              };
              const typeBadge =
                typeColors[sch.type?.toLowerCase()] ||
                "bg-gray-100 text-gray-600";

              return (
                <div
                  key={i}
                  className="relative group rounded-xl border border-slate-200 bg-white/90 backdrop-blur-sm p-4 shadow-[0_1px_3px_rgba(0,0,0,0.06)] hover:shadow-lg transition-all duration-200"
                >
                  {/* ✏️ Edit button — appears on hover only */}
                  <button
                    onClick={() => {
                      setSelectedActivity(sch);
                      setShowActivityModal(true);
                    }}
                    className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 bg-white/70 hover:bg-white text-slate-600 hover:text-slate-800 p-1 rounded-full shadow-sm transition-all"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>

                  {/* Header */}
                  <div className="flex justify-between items-start">
                    <div className="space-y-1">
                      <h3 className="text-sm font-semibold text-slate-800">
                        {sch.subject}
                      </h3>

                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className={`px-2 py-0.5 text-[11px] font-medium rounded-full capitalize ${typeBadge}`}
                        >
                          {sch.type}
                        </span>
                        <span className="text-[11px] text-gray-500">
                          Priority:{" "}
                          <span
                            className={`font-semibold ${
                              isHigh ? "text-red-500" : "text-emerald-600"
                            }`}
                          >
                            {sch.priority}
                          </span>
                        </span>
                      </div>
                    </div>

                    <span
                      className={`px-2 py-0.5 text-[11px] font-medium rounded-full capitalize mt-1 ml-2 ${
                        isCompleted
                          ? "bg-emerald-100 text-emerald-700"
                          : isPending
                            ? "bg-amber-100 text-amber-700"
                            : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {sch.status}
                    </span>
                  </div>

                  {/* Note Section */}
                  {sch.note && (
                    <p className="mt-2 text-sm text-slate-700 border-l-2 border-emerald-400 pl-2 leading-relaxed">
                      {sch.note}
                    </p>
                  )}

                  {/* Footer */}
                  <div className="mt-3 flex flex-wrap justify-between items-center text-[12px] text-gray-500 gap-2 border-t border-slate-100 pt-2">
                    <div className="flex items-center gap-1.5">
                      <Calendar className="h-3.5 w-3.5 text-slate-400" />
                      <span>{sch.due_date}</span>
                      <Clock className="h-3.5 w-3.5 text-slate-400 ml-2" />
                      <span>{sch.due_time}</span>
                    </div>

                    <div className="flex items-center gap-1.5">
                      <User className="h-3.5 w-3.5 text-slate-400" />
                      <span className="truncate">
                        Created by <b>{sch.user?.name || "—"}</b>
                      </span>
                    </div>

                    <div className="flex items-center gap-1.5">
                      <User className="h-3.5 w-3.5 text-slate-400" />
                      <span>
                        Assigned to <b>{sch.assignee?.name || "—"}</b>
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-gray-400 text-sm italic">
            No scheduled activities.
          </p>
        )}

        {/* ✅ Modal Integration */}
        {showActivityModal && (
          <EmployeeDirectoryAddUpdate
            onClose={() => {
              setShowActivityModal(false);
              setSelectedActivity({});
            }}
            data={selectedActivity}
            tabIndex={2}
            onError={onError}
            onSubmit={onSubmit}
            id={leadDetail?.userid}
            entitytype="contact"
            entityname={leadDetail?.name}
          />
        )}
      </div>
    </>
  );
};

// const Avatar = ({ user }) => {
//   const [imgError, setImgError] = React.useState(false);

//   if (!user?.userimg || imgError) {
//     return (
//       <div className="h-8 w-8 flex items-center justify-center rounded-full bg-gray-300 text-xs font-medium border-2 border-white shadow-sm">
//         {(
//           (user?.name?.split(" ")[0]?.[0] || "") +
//           (user?.name?.split(" ")[1]?.[0] || "")
//         ).toUpperCase()}
//       </div>
//     );
//   }

//   return (
//     <img
//       src={user?.userimg}
//       alt={user?.name}
//       onError={() => setImgError(true)}
//       className="h-8 w-8 rounded-full border-2 border-white object-cover shadow-sm bg-white"
//     />
//   );
// };

// Main Component
export default function AccountDetailMain({ defaultDealId }) {
  const { state } = useLocation();

  const dealIdFromLink = state?.defaultDealId ?? defaultDealId;

  console.log("defaultDealId", defaultDealId);

  const { id } = useParams();
  const [leadDetail, setLeadDetail] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [UsersList, setUsersList] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [entityData, setEntityData] = useState([]);
  const [alertData, setAlertData] = useState({ isShow: false });

  const [menuOpen, setMenuOpen] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [leftSidebarOpen, setLeftSidebarOpen] = useState(true);
  const [rightSidebarOpen, setRightSidebarOpen] = useState(true);
  const [dealCardOpen, setDealCardOpen] = useState(false);
  const [projectCardOpen, setProjectCardOpen] = useState(false);
  const [clientDeals, setClientDeals] = useState([]);

  const [activeDealId, setActiveDealId] = useState(null);
  const [activeProjectKey, setActiveProjectKey] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [search, setSearch] = useState("");
  const [productionUsers, setProductionUsers] = useState([]);

  const apiCall = useApiCall();

  const getData = async () => {
    try {
      const res = await apiCall({
        method: "get",
        url: `accounts/get-account/${id}`,
      });
      setLeadDetail(res?.data);
      setClientDeals(res?.data?.client_deals);

      setIsLoading(false);
    } catch (error) {
      console.warn("Error : ", error);
    }
  };

  useEffect(() => {
    getData();
  }, [id]);

  useEffect(() => {
    fetchUsers();
    fetchEntities();
  }, []);

  // onSave handler - updates backend & local state (optimistic)
  const handleFieldSave = async (key, newValue) => {
    // optimistic local update
    setLeadDetail((prev) => ({ ...prev, [key]: newValue }));

    try {
      const payload = { [key]: newValue };
      await apiCall({
        method: "put",
        url: `leads/update-field/${leadDetail?.leadid || id}`, // adapt endpoint
        data: payload,
      });
    } catch (err) {
      setLeadDetail((prev) => ({ ...prev, [key]: leadDetail?.[key] })); // resets to previous (you may persist previous to variable)
      throw err; // rethrow so EditableField shows error
    }
  };

  const fetchUsers = async () => {
    if (loadingUsers) return;
    setLoadingUsers(true);

    try {
      const res = await apiCall({
        method: "get",
        url: `users/agents-list`,
      });

      console.log("Users API Response ===>", res);
      const newUsers = res?.agents || res?.customers || res?.data || [];
      setUsersList(newUsers);
      setProductionUsers(res?.agents || []);
    } catch (err) {
      console.error("Error fetching users", err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchEntities = async (search = "") => {
    try {
      const res = await apiCall({
        method: "get",
        url: `activities/get-activity-type?search=${encodeURIComponent(
          search,
        )}`,
      });

      const list = res?.data || []; // 👈 safe access to data array

      setEntityData(
        list.map((item) => ({
          id: item.id,
          name: item.name,
          type: item.type,
        })),
      );
    } catch (error) {
      console.error("Error fetching entities:", error);
    }
  };

  const handleClose = () => {
    setAlertData({ isShow: false });
  };

  const [showEditModal, setShowEditModal] = useState(false); //
  const [currentData, setCurrentData] = useState({}); //
  const [DealStages, setDealStages] = useState([]); //
  const [Products, setProducts] = useState([]); //
  const [country, setCountry] = useState([]); //
  const [packages, setPackages] = useState([]); //
  const [PackageList, setPackageList] = useState([]);
  const [hasMoreClients, setHasMoreClients] = useState(true);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [Clients, setClients] = useState([]);
  const [page, setPage] = useState(1);
  const [showAddDealModal, setShowAddDealModal] = useState(false);
  const [showLead, setShowLead] = useState(false);
  const [subProducts, setSubProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [Menu, showMenu] = useState(false);
  const [dealDataLoading, setdealDataLoading] = useState(false);
  const [activeDealData, setActiveDealData] = useState({});
  const [activeProjectData, setActiveProjectData] = useState({});
  const [projectDataLoading, setProjectDataLoading] = useState(false);
  const [leftTabIndex, setLeftTabIndex] = useState(0);
  const [MainIndex, setMainIndex] = useState(0);

  const onSuccess = (message) => {
    setAlertData({ isShow: true, type: "success", message: message });
    setShowModal(false);
    setShowLead(false);
    setCurrentData({});
    getData();
    setMainIndex(0);
  };

  const updateData = (val, val2) => {
    setActiveDealData(val);
    setClientDeals(val2);
  };

  const onError = (message) => {
    setAlertData({ isShow: true, type: "error", message: message });
  };

  const updateSuccess = (message) => {
    setAlertData({ isShow: true, type: "success", message: message });
    setShowEditModal(false);
    setCurrentData({});
  };

  const editLead = (ele) => {
    setCurrentData(ele);
    setShowLead(true);
  };

  const updateError = (message) => {
    setAlertData({ isShow: true, type: "error", message: message });
  };

  const getProducts = async () => {
    try {
      const res = await apiCall({
        method: "get",
        url: "products/products-list?is_paginate=0",
      });

      setProducts(res?.data || []);
    } catch (error) {
      console.warn("Error fetching Products:", error);
    }
  };

  const DealStage = async () => {
    try {
      const res = await apiCall({
        method: "get",
        url: `dealstages/deal-stages-list`,
      });

      // filter out "Won" and "Lost"
      const filteredStages = res.data.filter(
        (stage) => stage.name !== "Won" && stage.name !== "Lost",
      );

      setDealStages(filteredStages);
    } catch (error) {
      console.warn("Error : ", error);
    }
  };

  const getPackages = async (subProductId) => {
    if (!subProductId) return;
    try {
      const res = await apiCall({
        method: "get",
        url: `products/get-sub-product-packages/${subProductId}`,
      });
      setPackages(res?.data || []);
    } catch (error) {
      console.error("Error fetching packages:", error);
    }
  };

  const getSubProducts = async (productId) => {
    if (!productId) return;
    try {
      const res = await apiCall({
        method: "get",
        url: `products/get-sub-products/${productId}`,
      });
      setSubProducts(res?.data || []);
    } catch (error) {
      console.error("Error fetching sub products:", error);
    }
  };

  const Countries = async () => {
    try {
      const res = await apiCall({
        method: "get",
        url: `location/get-countries`,
      });
      setCountry(res?.data);
    } catch (error) {
      console.warn("Error : ", error);
    }
  };

  const loadClients = async (pageNum = 1, search = "") => {
    if (isLoadingClients || !hasMoreClients) return;
    setIsLoadingClients(true);
    try {
      const res = await apiCall({
        method: "get",
        url: `users/customers-list?page=${pageNum}&limit=10&search=${encodeURIComponent(
          search,
        )}`,
      });

      const newClients = res?.customers || [];
      if (pageNum === 1) {
        setClients(newClients); // replace results when searching
      } else if (newClients.length === 0) {
        setHasMoreClients(false);
      } else {
        setClients((prev) => [...prev, ...newClients]);
        setPage(pageNum);
      }
    } catch (err) {
      console.error("Error fetching users", err);
    } finally {
      setIsLoadingClients(false);
    }
  };

  const getBrands = async () => {
    try {
      const res = await apiCall({
        method: "get",
        url: "brands/brands-list?is_paginate=0",
      });

      setBrands(res?.data || []);
    } catch (error) {
      console.warn("Error fetching brands:", error);
    }
  };

  const getActiveProject = async (key) => {
    try {
      setProjectDataLoading(true);
      const res = await apiCall({
        method: "get",
        url: `projects/get-project/${key}`,
      });

      setActiveProjectData(res?.project || []);
    } catch (error) {
      console.warn("Error fetching brands:", error);
    } finally {
      setProjectDataLoading(false);
    }
  };
  const getActiveDeal = async (id) => {
    try {
      setdealDataLoading(true);
      const res = await apiCall({
        method: "get",
        url: `deals/get-deal/${id}`,
      });

      setActiveDealData(res?.data || []);
    } catch (error) {
      console.warn("Error fetching brands:", error);
    } finally {
      setdealDataLoading(false);
    }
  };

  useEffect(() => {
    getBrands();
    getProducts();
    loadClients();
    DealStage();
    fetchUsers();
    Countries();
    getPackages();
    getSubProducts();
  }, []);

  const TAB_CONFIG = [
    {
      key: "details",
      label: "Details",
      component: <Details leadDetail={leadDetail} />,
    },
    {
      key: "transactions",
      label: "Transactions",
      component: <TransactionTabs leadDetail={leadDetail} />,
    },
    {
      key: "followups",
      label: "Follow ups",
      component: (
        <FollowUpsTab
          leadDetail={leadDetail}
          onSubmit={onSuccess}
          onError={onError}
        />
      ),
    },
    {
      key: "timeline",
      label: "Timeline",
      component: <TimelineTab leadDetail={leadDetail} />,
    },
    {
      key: "comments",
      label: "Comments",
      component: <CommentsTab leadDetail={leadDetail} />,
    },
    {
      key: "files",
      label: "Files",
      component: (
        <FilesTab
          leadId={leadDetail?.userid}
          attachments={leadDetail?.attachments}
          attachmenttype="contact"
        />
      ),
    },
  ].filter(Boolean);

  const [tab, setTab] = useState(0);
  const [show, setShow] = useState(false);

  const [tabsCustomizeTabs, setTabsCustomizeTabs] = useState({
    details: true,
    transactions: true,
    followups: true,
    timeline: true,
    comments: true,
    followups: true,
    files: true,
  });

  const handleTabChange = (index) => {
    setTab(index);
  };

  const renderedTabs = useMemo(() => {
    return TAB_CONFIG.map((tabItem, index) => {
      if (!tabsCustomizeTabs[tabItem.key]) return null;
      return (
        <li
          key={tabItem.key}
          className={`${tab === index ? Style.active : null}`}
          onClick={() => {
            if (tabItem?.component) {
              handleTabChange(index);
              setDealCardOpen(false);
              setProjectCardOpen(false);
            }
          }}
        >
          <div className={Style.icon}></div>
          <span>{tabItem.label}</span>
        </li>
      );
    });
  }, [tab, tabsCustomizeTabs]);

  const dealDetail = (dealid) => {
    if (MainIndex === 1 && activeDealId === dealid) {
      // Already viewing this deal → go back
      setMainIndex(0);
      setDealCardOpen(false);
      return;
    }

    // Otherwise open new deal detail
    setActiveDealId(dealid);
    getActiveDeal(dealid);
    setMainIndex(1);
    setProjectCardOpen(false);
    setDealCardOpen(true);
  };

  // useEffect(() => {
  //   if (dealIdFromLink) {
  //     dealDetail(dealIdFromLink);
  //   }
  // }, [dealIdFromLink]);

  const projectDetail = (projectKey) => {
    if (MainIndex === 2 && activeProjectKey === projectKey) {
      // Already viewing this project → go back
      setMainIndex(0);
      setProjectCardOpen(false);
      return;
    }

    // Otherwise open new project detail
    setActiveProjectKey(projectKey);
    getActiveProject(projectKey);
    setMainIndex(2);
    setProjectCardOpen(true);
    setDealCardOpen(false);
  };

  const [showAll, setShowAll] = useState(false);

  const users = Array.isArray(leadDetail?.assigned_users)
    ? leadDetail?.assigned_users
    : [];

  const visibleUsers = showAll ? users : users.slice(0, 2);
  const hiddenCount = users.length - 3;

  const remainingUsers = useMemo(() => {
    if (productionUsers?.length == 0) return [];

    const assignedUserIds = new Set(users?.map((u) => u?.userid));

    return productionUsers?.filter(
      (user) => !assignedUserIds.has(user?.userid),
    );
  }, [users, productionUsers]);

  const filteredUsers = useMemo(() => {
    if (!search.trim()) return remainingUsers;

    return remainingUsers.filter((u) =>
      u.name.toLowerCase().includes(search.toLowerCase()),
    );
  }, [search, remainingUsers]);

  const addAssigned = async (user) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to assigned this agent?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) {
      console.log("User cancelled");
      return;
    }

    let data = {
      client_id: leadDetail?.userid,
      agent_id: user?.userid,
    };

    try {
      const url = `users/assign-agent-to-client`;

      const apiRes = await apiCall({
        method: "post",
        url,
        data,
      });

      Swal.fire({
        icon: "success",
        title: apiRes?.message || "Assigned Agent Successfully",
        timer: 1500,
      });

      setShowDropdown(false);
      getData();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text:
          error?.response?.data?.respMsg ||
          error?.response?.data?.responseMessage ||
          error?.response?.data?.message ||
          "Something went wrong",
      });
      setShowDropdown(false);
    }
  };

  const deleteAssigned = async (user) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to remove this agent into assigned?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) {
      console.log("User cancelled");
      return;
    }

    let data = {
      client_id: leadDetail?.userid,
      agent_id: user?.userid,
      type: "contact",
    };

    try {
      const url = `users/un-assign-agent-to-client`;

      const apiRes = await apiCall({
        method: "post",
        url,
        data,
      });

      Swal.fire({
        icon: "success",
        title: apiRes?.message || "UnAssigned Agent Successfully",
        timer: 1500,
      });

      setShowDropdown(false);
      setShowAll(false);
      getData();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text:
          error?.response?.data?.respMsg ||
          error?.response?.data?.responseMessage ||
          error?.response?.data?.message ||
          "Something went wrong",
      });
      setShowDropdown(false);
    }
  };

  const menuRef = useRef(null);

  useClickOutside(menuRef, () => {
    showMenu(false);
  });


  const assignedRef = useRef(null);

  useClickOutside(assignedRef, () => {
    setShowAll(false);
  });

  const assignedDropdownRef = useRef(null);

  useClickOutside(assignedDropdownRef, () => {
    setShowDropdown(false);
  });

  return (
    <>
      {isLoading ? (
        <InnerLoader />
      ) : (
        <TableCard
          className="mt-[20px] relative !overflow-hidden"
          scrollClassName={"!overflow-hidden"}
        >
          <div className="flex w-full h-full overflow-hidden">
            {!leftSidebarOpen && (
              <button
                onClick={() => setLeftSidebarOpen(true)}
                className="absolute left-2 top-2 z-10 bg-white rounded-lg p-2 shadow-md hover:bg-gray-50 transition-colors border border-[#e1e4ea]"
                aria-label="Open left sidebar"
              >
                <PanelLeft className="w-5 h-5 text-[#666]" />
              </button>
            )}

            {/* left side Section */}
            <div
              className={`
                transition-all duration-300 ease-in-out shrink-0 
               
                ${leftSidebarOpen ? "w-[350px]" : "w-0 !p-0"}
              `}
            >
              <div className="p-2 flex flex-col gap-4 overflow-hidden overflow-y-auto h-full">
                <div className="rounded-[10px] bg-white border border-[#E1E4EA] py-[8px] px-[8px] flex-shrink-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-[10px]">
                      {[{ label: "Deals" }, { label: "Projects" }].map(
                        (item, idx) => {
                          const activeleftBar = idx === leftTabIndex; // first tab active

                          return (
                            <button
                              key={idx}
                              className={`px-[25px] py-[7px] flex items-center gap-1 text-[14px] text-[#414651] font-medium rounded-[10px] transition-all border
                                  ${
                                    activeleftBar
                                      ? "bg-[#F6F6F6] border-[#DDD]"
                                      : "bg-[#ffffff] border-[#e1e4ea]"
                                  }    
                                                            `}
                              onClick={() => {
                                setLeftTabIndex(idx);
                              }}
                            >
                              <span>{item.label}</span>
                            </button>
                          );
                        },
                      )}
                    </div>

                    {/* left side filter icon */}
                    <div className="text-[#B7B8BB] text-xl cursor-pointer transition-all hover:text-gray-600 flex gap-1 items-center">
                      <button
                        onClick={() => {
                          setLeftSidebarOpen(false);
                        }}
                        className="p-1 rounded-md bg-blue-600 text-white 
                            hover:bg-blue-700 hover:shadow-md 
                            active:scale-95 
                            transition-all duration-200 group"
                      >
                        <ChevronLeft
                          size={18}
                          className="transition-transform duration-200 group-hover:-translate-x-1"
                        />
                      </button>
                    </div>
                  </div>
                </div>

                {leftTabIndex == 0 ? (
                  <>
                    {/* Deals */}
                    <div className="gap-y-[10px] h-full bg-white  py-5 px-[15px] rounded-[10px] border border-[#E1E4EA] overflow-hidden">
                      <div className="text-xl leading-9 font-medium overflow-hidden flex justify-between items-center">
                        <span>Deals</span>
                      </div>

                      <div className="h-[1px] bg-[#E1E4EA] w-full rounded-[10px] my-[10px]"></div>

                      <div className="flex flex-col gap-y-[10px]  mt-[10px] max-h-[85%] min-h-0  overflow-auto">
                        {clientDeals?.map((eachList, i) => {
                          return (
                            <div
                              className={`p-[15px] rounded-[10px] cursor-pointer border border-[#E1E4EA] transition-all duration-200 hover:bg-[#f1f1f1]  ${
                                activeDealData.dealid == eachList.dealid &&
                                dealCardOpen
                                  ? "bg-[#F3F3F3]"
                                  : "bg-transparent"
                              }`}
                              onClick={() => {
                                dealDetail(eachList?.dealid);
                              }}
                            >
                              <div className="flex justify-between items-center cursor-pointer">
                                {/* Deal Info */}
                                <div className="flex gap-x-1">
                                  <div className="flex flex-col">
                                    <div className="text-[14px] leading-5 font-medium">
                                      {eachList?.name}
                                    </div>
                                    <div className="text-[8px] ">
                                      {eachList?.deal_package}
                                    </div>
                                  </div>
                                </div>

                                {/* Icons */}
                                <div>
                                  {eachList?.current_stage?.dealstatus ==
                                  "Need Analysis" ? (
                                    <Chip
                                      color={"yellow"}
                                      className="text-nowrap !text-[11px] cursor-pointer"
                                    >
                                      Need Analysis
                                    </Chip>
                                  ) : eachList?.current_stage?.dealstatus ==
                                    "Value Proposition" ? (
                                    <Chip
                                      color="blue"
                                      className="text-nowrap !text-[11px] cursor-pointer"
                                    >
                                      Value Proposition
                                    </Chip>
                                  ) : eachList?.current_stage?.dealstatus ==
                                    "Identify Decision" ? (
                                    <Chip
                                      color="violet"
                                      className="text-nowrap !text-[11px] cursor-pointer"
                                    >
                                      Identify Decision
                                    </Chip>
                                  ) : eachList?.current_stage?.dealstatus ==
                                    "Proposal" ? (
                                    <Chip
                                      color="teal"
                                      className="!text-[11px] cursor-pointer"
                                    >
                                      Proposal
                                    </Chip>
                                  ) : eachList?.current_stage?.dealstatus ==
                                    "Review" ? (
                                    <Chip
                                      color="indigo"
                                      className="!text-[11px] cursor-pointer"
                                    >
                                      Review
                                    </Chip>
                                  ) : eachList?.current_stage?.dealstatus ==
                                    "Won" ? (
                                    <Chip
                                      color="green"
                                      className="!text-[11px] cursor-pointer"
                                    >
                                      Won
                                    </Chip>
                                  ) : eachList?.current_stage?.dealstatus ==
                                    "Lost" ? (
                                    <Chip
                                      color="red"
                                      className="!text-[11px] cursor-pointer"
                                    >
                                      Lost
                                    </Chip>
                                  ) : (
                                    <Chip
                                      color="gray"
                                      className="!text-[11px] cursor-pointer"
                                    >
                                      {eachList?.current_stage?.dealstatus ||
                                        "--"}
                                    </Chip>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </>
                ) : leftTabIndex == 1 ? (
                  <>
                    {/* Projects */}

                    <div className="gap-y-[10px] h-full bg-white  py-5 px-[15px] rounded-[10px] border border-[#E1E4EA] overflow-hidden">
                      <div className="text-xl leading-9 font-medium overflow-hidden flex justify-between items-center">
                        <span>Projects</span>
                      </div>

                      <div className="h-[1px] bg-[#E1E4EA] w-full rounded-[10px] my-[10px]"></div>

                      <div className="flex flex-col gap-y-[10px]  mt-[10px] max-h-[85%] min-h-0  overflow-auto">
                        {leadDetail?.client_projects?.map((eachList, i) => {
                          return (
                            <div
                              className={`p-[15px] cursor-pointer rounded-[10px] border border-[#E1E4EA] transition-all duration-200 hover:bg-[#f1f1f1] ${
                                activeProjectData.projectid ==
                                  eachList.projectid && projectCardOpen
                                  ? "bg-[#F3F3F3]"
                                  : "bg-transparent"
                              }`}
                              onClick={() => {
                                projectDetail(eachList?.projectkey);
                              }}
                            >
                              <div className="flex justify-between items-center cursor-pointer">
                                {/* Canditate Info */}
                                <div className="flex gap-x-2  ">
                                  <div className="flex flex-col">
                                    <div className="text-[14px] leading-5 font-medium">
                                      {eachList?.invoiceid}
                                    </div>
                                    <div className="text-[8px] ">
                                      {eachList?.startdate}
                                    </div>
                                  </div>
                                </div>

                                {/* Icons */}
                                <div>
                                  <StatusChip
                                    className="cursor-pointer"
                                    color={
                                      eachList.status === "success"
                                        ? "green"
                                        : eachList.status === "pending"
                                          ? "blue"
                                          : "red"
                                    }
                                  >
                                    {eachList.status}
                                  </StatusChip>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </>
                ) : null}
              </div>
            </div>

            {/* Middle Section */}

            <div className="flex flex-col gap-[10px] pt-2 w-full h-full overflow-hidden">
              <div className="bg-white   p-[15px] rounded-[10px] border border-[#E1E4EA]   w-full ">
                {/* Top Middle Sections */}
                <div className="flex justify-between">
                  <div className="flex  items-center">
                    <div className="flex flex-col gap-3">
                      <div className="flex gap-1">
                        <Chip2
                          label={
                            leadDetail?.verified == 0
                              ? "Not Verified"
                              : "Verified"
                          }
                          tone={
                            leadDetail?.verified == 0 ? "warning" : "success"
                          }
                        />
                        <div className="flex justify-center gap-1 items-center text-sm font-medium h-full">
                          <span className="text-[#0E121B]">•</span>
                          <span className="text-[#0E121B] capitalize">
                            {leadDetail?.active ? "Active" : "In Active"}
                          </span>
                        </div>
                      </div>

                      <div className="text-2xl font-medium">
                        {leadDetail?.name}
                      </div>

                      <div className="text-sm font-normal text-[#84CC16]">
                        {[
                          leadDetail?.country,
                          leadDetail?.state,
                          leadDetail?.city,
                        ]
                          .filter((v) => v && v !== "-")
                          .join(", ")}
                      </div>

                      <div className="text-xs leading-4 font-normal">
                        zip code: {leadDetail?.zipcode || "-"}
                      </div>
                    </div>

                    <div className="px-[30px] flex flex-col gap-2">
                      <div className="flex flex-col gap-y-2">
                        <div className="text-xs font-normal text-[rgba(14,18,27,0.40)]">
                          Phone
                        </div>
                        <a
                          href={`tel:${leadDetail?.phone}`}
                          className="flex items-center gap-1 hover:text-emerald-600"
                        >
                          <PhoneCall className="h-4 w-4" />
                          {leadDetail?.phone || "-"}
                        </a>
                      </div>
                      <div className="flex flex-col gap-y-2">
                        <div className="text-xs font-normal text-[rgba(14,18,27,0.40)]">
                          Email
                        </div>
                        <a
                          href={`mailto:${leadDetail?.email}`}
                          className="flex items-center gap-1 hover:text-sky-600"
                        >
                          <Mail className="h-4 w-4" />
                          {leadDetail?.email || "-"}
                        </a>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col justify-between items-end">
                    <div className="flex items-center gap-1 z-10">
                      <div className="" ref={menuRef}>
                        <div
                          className="transition-all duration-300 hover:text-gray-500 relative cursor-pointer"
                          onClick={() => {
                            showMenu(!Menu);
                          }}
                        >
                          <DotsVerticalIcon />

                          {Menu ? (
                            <div
                              className={`border w-48 shadow  border-[#e4e4e4] bg-white p-2 rounded  absolute right-0 flex flex-col gap-[2px] cursor-pointer  ${
                                Menu ? "opacity-1" : "opacity-0"
                              }`}
                            >
                              <button
                                onClick={() => {
                                  setShowAddDealModal(true);
                                }}
                                className={`w-full flex items-center gap-2 rounded-lg  px-3 py-2 text-sm font-medium text-black hover:bg-[#efefef]  transition ${
                                  ["Won", "Lost"].includes(
                                    leadDetail?.current_stage?.dealstatus,
                                  )
                                    ? "opacity-50 cursor-not-allowed pointer-events-none"
                                    : ""
                                }`}
                              >
                                <Plus className="h-4 w-4" />
                                Add Deal
                              </button>

                              <button
                                onClick={() => {
                                  if (
                                    typeof setSelectedActivity === "function"
                                  ) {
                                    setSelectedActivity(null);
                                  }
                                  if (
                                    typeof setShowActivityModal === "function"
                                  ) {
                                    setShowActivityModal(true);
                                  }
                                  setMenuOpen(false);
                                }}
                                className="flex items-center gap-2 rounded-lg text-black  hover:bg-[#efefef]  px-3 py-2 text-sm font-medium  transition"
                              >
                                <Plus className="h-4 w-4" />
                                Add Follow-Ups
                              </button>

                              <button
                                onClick={() => {
                                  editLead(leadDetail);
                                }}
                                className="flex items-center gap-2 rounded-lg  text-black hover:bg-[#efefef]  px-3 py-2 text-sm font-medium  transition"
                              >
                                <NewEditIcon className="h-4 w-4" />
                                Edit Account
                              </button>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>

                    {/* Assigned Members */}

                    <div className="flex items-center justify-end">
                      <div className="flex justify-end flex-col items-end">
                        <p className="font-normal leading-6">
                          Assigned Members
                        </p>

                        <div className="flex  gap-1 items-center">
                          <div className="relative " ref={assignedRef}>
                            <div
                              className="flex -space-x-2 cursor-pointer"
                              onClick={() => {
                                setShowAll(!showAll);
                                setShowDropdown(false);
                              }}
                            >
                              {leadDetail?.assigned_users
                                ?.slice(0, 3)
                                .map((user) => (
                                  // <Avatar key={user.id} user={user} />
                                  <Avatar
                                    src={user?.userimg}
                                    title={user?.name}
                                    className="border-2 border-gray-200 shadow-sm "
                                  />
                                ))}

                              {hiddenCount > 0 && (
                                <div className="h-8 w-8 flex items-center justify-center rounded-full bg-[#0057FB] text-white text-xs font-medium border-2 border-white shadow-sm">
                                  +{hiddenCount}
                                </div>
                              )}
                            </div>

                            {/* show add assigned user dropdown */}

                            {showAll && (
                              <div className="absolute top-9   right-0 w-60 border border-[#f6f6f6]  bg-white rounded-xl shadow-xl p-3 z-30 overflow-hidden">
                                <div className="flex justify-between items-center mb-2  p-1 border-gray-400 border-b">
                                  <h4 className="text-sm font-medium">
                                    Assigned Agents
                                  </h4>
                                  <button
                                    onClick={() => setShowAll(false)}
                                    className="p-1 rounded hover:bg-gray-100 transition"
                                  >
                                    <X className="w-4 h-4 text-gray-500 hover:text-black" />
                                  </button>
                                </div>

                                <div className="!overflow-y-auto w-full max-h-52  h-full">
                                  {leadDetail?.assigned_users?.map(
                                    (each, idx) => (
                                      <div
                                        key={idx}
                                        className="flex items-center justify-between gap-2 p-1 hover:bg-slate-100 transition-all duration-300"
                                      >
                                        <AvatarProfile
                                          src={each?.userimg}
                                          title={each?.name}
                                          className="text-sm"
                                        />

                                        <span className="flex justify-end">
                                          <Tooltip
                                            key={`${idx}-unassigned`}
                                            className={`${Style.Tooltip}`}
                                            boxClassName={Style.TooltipBox}
                                            content="Delete"
                                            position={"bottom center"}
                                          >
                                            <div
                                              onClick={() => {
                                                deleteAssigned(each);
                                              }}
                                              className="p-2 text-red-600"
                                            >
                                              <Trash2 size={18} />
                                            </div>
                                          </Tooltip>
                                        </span>
                                      </div>
                                    ),
                                  )}
                                </div>
                              </div>
                            )}
                          </div>

                          <div>
                            <div
                              className="projectDetail-cardLeftUserAddBtn selectUser relative"
                              ref={assignedDropdownRef}
                            >
                              <button
                                className="bg-[#0057FB] text-white flex text-[12px] p-1 rounded-full items-center gap-1 selectUser-btn cursor-pointer"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowDropdown(!showDropdown);
                                  setShowAll(false);
                                }}
                              >
                                {" "}
                                <Plus className="h-5 w-5" />
                              </button>

                              {/* Dropdown */}
                              {showDropdown && (
                                <div className="selectUser-view customSearch mt-2 border border-[#f6f6f6] shadow-xl p-3 rounded-xl  bg-white w-64 absolute right-0  z-20">
                                  <div className="selectUser-search flex items-center gap-2  mb-2 border-gray-400 border-b pb-1">
                                    <div className="icon">
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="18"
                                        height="18"
                                        viewBox="0 0 18 18"
                                        fill="none"
                                      >
                                        <mask
                                          id="mask1"
                                          style={{ maskType: "alpha" }}
                                          maskUnits="userSpaceOnUse"
                                          x="0"
                                          y="0"
                                          width="18"
                                          height="18"
                                        >
                                          <rect
                                            width="18"
                                            height="18"
                                            fill="#D9D9D9"
                                          ></rect>
                                        </mask>
                                        <g mask="url(#mask1)">
                                          <path
                                            d="M19.6 21L13.3 14.7C12.8 15.1 12.225 15.4167 11.575 15.65C10.925 15.8833 10.2333 16 9.5 16C7.68333 16 6.14583 15.3708 4.8875 14.1125C3.62917 12.8542 3 11.3167 3 9.5C3 7.68333 3.62917 6.14583 4.8875 4.8875C6.14583 3.62917 7.68333 3 9.5 3C11.3167 3 12.8542 3.62917 14.1125 4.8875C15.3708 6.14583 16 7.68333 16 9.5C16 10.2333 15.8833 10.925 15.65 11.575C15.4167 12.225 15.1 12.8 14.7 13.3L21 19.6L19.6 21ZM9.5 14C10.75 14 11.8125 13.5625 12.6875 12.6875C13.5625 11.8125 14 10.75 14 9.5C14 8.25 13.5625 7.1875 12.6875 6.3125C11.8125 5.4375 10.75 5 9.5 5C8.25 5 7.1875 5.4375 6.3125 6.3125C5.4375 7.1875 5 8.25 5 9.5C5 10.75 5.4375 11.8125 6.3125 12.6875C7.1875 13.5625 8.25 14 9.5 14Z"
                                            fill="#999999"
                                          ></path>
                                        </g>
                                      </svg>
                                    </div>
                                    <input
                                      type="text"
                                      placeholder="Search Agent..."
                                      className="customSearchInput border rounded px-2 py-1 w-full"
                                      value={search}
                                      onChange={(e) =>
                                        setSearch(e.target.value)
                                      }
                                    />
                                  </div>

                                  <div className="selectUser-list max-h-48 overflow-y-auto">
                                    <ul className="customSearchList ProjectAssignUsersList">
                                      {filteredUsers?.map((user) => (
                                        <li
                                          key={user?.userid}
                                          className="UserForAssign flex items-center gap-2 p-1 cursor-pointer hover:bg-gray-100 rounded"
                                          onClick={() => {
                                            addAssigned(user);
                                          }}
                                        >
                                          <AvatarProfile
                                            src={user?.userimg}
                                            title={user?.name}
                                            className="text-sm"
                                          />
                                        </li>
                                      ))}
                                      {loadingUsers && (
                                        <li className="p-2 text-center text-gray-400 text-sm">
                                          Loading...
                                        </li>
                                      )}
                                      {filteredUsers?.length == 0 && (
                                        <li className="p-1 text-gray-400">
                                          No users found
                                        </li>
                                      )}
                                    </ul>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {MainIndex == 0 ? (
                <>
                  <div>
                    <div>
                      <div className={Style.modal_header}>
                        <div className={Style.modal_nav}>
                          <ul>{renderedTabs}</ul>
                        </div>
                        <div
                          className="flex gap-[2px] justify-center cursor-pointer text-[#999]"
                          onClick={() => setShow(true)}
                        >
                          <Settings2 size={20} />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col  h-screen overflow-auto">
                    {TAB_CONFIG.map(
                      (tabItem, index) =>
                        tab === index && (
                          <Suspense
                            key={tabItem?.key}
                            fallback={
                              <div className="tableDataLoader">
                                <div className="tableDataLoader-wrapper">
                                  <div className="loader"></div>
                                  <span>Data Load...</span>
                                </div>
                              </div>
                            }
                          >
                            {tabItem?.component}
                          </Suspense>
                        ),
                    )}
                  </div>
                </>
              ) : MainIndex == 1 ? (
                <DealInfo
                  ProductList={Products}
                  // PackageList={PackageList}
                  DealStages={DealStages}
                  ClientList={Clients}
                  UserList={UsersList}
                  leadDetail={leadDetail}
                  activeDealData={activeDealData}
                  loading={dealDataLoading}
                  clientDeals={clientDeals}
                  onSuccess={onSuccess}
                  onError={onError}
                  updateData={updateData}
                />
              ) : MainIndex == 2 ? (
                <ProjectTab
                  leadDetail={leadDetail}
                  activeProjectData={activeProjectData}
                  loading={projectDataLoading}
                  onSuccess={onSuccess}
                  onError={onError}
                />
              ) : null}
            </div>

            {/* Right Sidebar */}
            <RightSidebar
              isOpen={rightSidebarOpen}
              onToggle={() => setRightSidebarOpen(false)}
            />

            {/* Right Sidebar Toggle Button - Shows when sidebar is closed */}
            {!rightSidebarOpen && (
              <button
                onClick={() => setRightSidebarOpen(true)}
                className="absolute right-2 top-2 z-10 bg-white rounded-lg p-2 shadow-md hover:bg-gray-50 transition-colors border border-[#e1e4ea]"
                aria-label="Open right sidebar"
              >
                <PanelRight className="w-5 h-5 text-[#666]" />
              </button>
            )}
          </div>
        </TableCard>
      )}

      {showActivityModal && (
        <EmployeeDirectoryAddUpdate
          onClose={() => {
            setShowActivityModal(false);
          }}
          data={null}
          tabIndex={2}
          onError={onError}
          onSubmit={onSuccess}
          id={leadDetail?.leadid}
          entitytype="lead"
          entityname={leadDetail.name}
        />
      )}

      {showAddDealModal && (
        <EmployeeDirectoryAddUpdate
          open={showAddDealModal}
          onClose={() => {
            setShowAddDealModal(false);
          }}
          data={{ contactid: leadDetail?.userid }}
          tabIndex={1}
          contactId={leadDetail?.userid}
          onDealUpdated={() => getData()} // refresh after update
          onSubmit={onSuccess}
          onError={onError}
          type="account"
        />
      )}

      {showLead && (
        <EmployeeDirectoryAddUpdate
          onClose={() => {
            setShowLead(false);
            setCurrentData({});
          }}
          data={currentData}
          tabIndex={0}
          onError={onError}
          onSubmit={onSuccess}
        />
      )}

      <Snackbar
        open={alertData?.isShow}
        autoHideDuration={3000}
        onClose={handleClose}
      >
        <Alert
          onClose={handleClose}
          severity={alertData?.type}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {alertData?.message}
        </Alert>
      </Snackbar>

      {show && (
        <CustomizeTabsOverviewPopup
          data={TAB_CONFIG.map((t) => t)}
          tabs={tabsCustomizeTabs}
          onClose={() => setShow(false)}
          onToggle={(tabName) => {
            setTabsCustomizeTabs((prev) => ({
              ...prev,
              [tabName]: !prev[tabName],
            }));
          }}
        />
      )}
    </>
  );
}
